import { useQuery } from '@tanstack/react-query';
import { lazy, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { generalConfigApi } from '../../apis';
import SuspenseWrap from '../../components/Loading/SuspenseWrap';
import AuthRoute from '../../components/RouteWrap/AuthRoute';
import ProtectRoute from '../../components/RouteWrap/ProtectRoute';
import { CONFIG_LOGO } from '../../constants/QueryKey';
import { ADMIN_ROUTE_NAME } from '../../constants/RouteName';
import { useAppDispatch } from '../../store';
import { updateLogo } from '../../store/settingSlice';
import CommonComponents from '../CommonComponents';
import EmployeeApplication from '../Employee/EmployeeApplication';
import AnnualOvertime from '../Manager/AnnualOvertime';
import { AnnualOvertimeDetail } from '../Manager/AnnualOvertime/AnnualOvertimeDetail';
import ApplycationApproval from '../Manager/ApplycationApproval';
import AttendanceConfirm from '../Manager/AttendanceConfirm';
import AttendanceManagement from '../Manager/AttendanceManagement';
import NotFound from '../Notfound';
import EmployeeConfirm from './../Employee/EmployeeConfirm/index';
import SignInAdmin from './Auth/SignIn';
import { BookingManagement } from './BookingManagement';
import ChoiceSettings from './ChoiceSettings';
import ApplicationApproval from './Configuration/ApplicationApprovalFun';
import Holiday from './Configuration/Holiday';
import OverTime from './Configuration/OverTime';
import SettingMessage from './Configuration/SettingMessage';
import TimeCorrection from './Configuration/TimeCorrection';
import UploadLogo from './Configuration/UpLoadLogo';
import WorkHours from './Configuration/WorkHours';
import Admin from './index';
import Location from './LocationManagement/Location';
import LocationList from './LocationManagement/LocationList';
import PrivacyPolicy from './PrivacyPolicy';
import ScheduleManagement from './ScheduleManagement';
import DepartmentManagement from './StaffDepartmentPosition/DepartmentManagement';
import PositionManagement from './StaffDepartmentPosition/PositionManagement';
import StaffTypeManagement from './StaffDepartmentPosition/StaffTypeManagement';
import StaffManagement from './StaffManagement';
import TermsOfService from './TermsOfService';
import WorkingHours from './WorkingHour';
import EmployeeAttendance from '../Employee/EmployeeAttendance';
import HomePage from './HomePage';
import TimekeepingReminder from './Configuration/TimekeepingReminder';
import Contact from '../Contact';

const AccountLink = lazy(() => import('./AccountLink'));

const AdminRoutes = () => {
  const dispatch = useAppDispatch();

  const { data: logoConfigResponse } = useQuery({
    queryKey: [CONFIG_LOGO],
    queryFn: () => generalConfigApi.generalConfigControllerGetLogoConfig(),
  });

  useEffect(() => {
    if (logoConfigResponse?.data) {
      const logo = (logoConfigResponse?.data as { data: { url: string } })?.data?.url;

      dispatch(updateLogo(logo || 'images/mos-main.png'));
    }
  }, [logoConfigResponse?.data, dispatch]);

  return (
    <SuspenseWrap
      component={
        <Routes>
          {/* AuthRoute */}
          {/* using AuthRoute when the route belongs to the authentication route */}
          <Route element={<AuthRoute />}>
            <Route path={ADMIN_ROUTE_NAME.HOMEPAGE} element={<HomePage />} />
            <Route path={ADMIN_ROUTE_NAME.SIGNIN} element={<SignInAdmin />} />
          </Route>

          {/* ProtectRoute */}
          {/* using ProtectRoute when the route needs user to sign in to access it */}
          <Route element={<ProtectRoute />}>
            <Route path="" element={<Admin />}>
              <Route path={ADMIN_ROUTE_NAME.BOOKING} element={<BookingManagement />} />
              <Route path={ADMIN_ROUTE_NAME.STAFF_MANAGEMENT} element={<StaffManagement />} />
              <Route path={ADMIN_ROUTE_NAME.STAFF_TYPE_MANAGEMENT} element={<StaffTypeManagement />} />
              <Route path={ADMIN_ROUTE_NAME.DEPARTMENT_MANAGEMENT} element={<DepartmentManagement />} />
              <Route path={ADMIN_ROUTE_NAME.POSITION_MANAGEMENT} element={<PositionManagement />} />
              <Route path={ADMIN_ROUTE_NAME.SCHEDULE} element={<ScheduleManagement />} />
              <Route path={ADMIN_ROUTE_NAME.LOCATION} element={<Location />} />
              <Route path={ADMIN_ROUTE_NAME.LOCATION_LIST} element={<LocationList />} />
              <Route path={ADMIN_ROUTE_NAME.HOLIDAY} element={<Holiday />} />
              <Route path={ADMIN_ROUTE_NAME.AFFILIATE_ACCOUNT} element={<AccountLink />} />
              <Route path={ADMIN_ROUTE_NAME.LOGO} element={<UploadLogo />} />
              <Route path={ADMIN_ROUTE_NAME.CHOICE_SETTINGS} element={<ChoiceSettings />} />
              <Route path={ADMIN_ROUTE_NAME.WORKING_SCHEDULE} element={<WorkingHours />} />
              <Route path={ADMIN_ROUTE_NAME.WORKING_HOURS} element={<WorkHours />} />
              <Route path={ADMIN_ROUTE_NAME.OVERTIME_HOURS} element={<OverTime />} />
              <Route path={ADMIN_ROUTE_NAME.REQUEST_REVIEW} element={<ApplicationApproval />} />
              <Route path={ADMIN_ROUTE_NAME.SETTING_MESSAGE} element={<SettingMessage />} />
              <Route path={ADMIN_ROUTE_NAME.ATTENDANCE_CONFIRMATION} element={<TimeCorrection />} />
              <Route path={ADMIN_ROUTE_NAME.PRIVACY_POLICY} element={<PrivacyPolicy />} />
              <Route path={ADMIN_ROUTE_NAME.TERMS_OF_SERVICE} element={<TermsOfService />} />
              <Route path={ADMIN_ROUTE_NAME.TIMEKEEPING_REMINDER} element={<TimekeepingReminder />} />

              {/* Employee */}
              <Route path={ADMIN_ROUTE_NAME.EMPLOYEE_ATTENDANCE} element={<EmployeeAttendance />} />
              <Route path={ADMIN_ROUTE_NAME.EMPLOYEE_CONFIRM} element={<EmployeeConfirm />} />
              <Route path={ADMIN_ROUTE_NAME.EMPLOYEE_APPLICAIION} element={<EmployeeApplication />} />
              {/* Manager */}
              <Route path={ADMIN_ROUTE_NAME.ATTENDANCE_MANAGEMENT} element={<AttendanceManagement />} />
              <Route path={ADMIN_ROUTE_NAME.ATTENDANCE_MANAGEMENT_DETAIL} element={<AttendanceConfirm />} />
              <Route path={ADMIN_ROUTE_NAME.TIMEKEEPING_CONFIRMATION} element={<AttendanceConfirm />} />
              <Route path={ADMIN_ROUTE_NAME.APPLY_FOR_RECOGNITION} element={<ApplycationApproval />} />
              <Route path={ADMIN_ROUTE_NAME.OUT_OF_YEAR_MANAGEMENT} element={<AnnualOvertime />} />
              <Route path={ADMIN_ROUTE_NAME.OUT_OF_YEAR_MANAGEMENT_DETAIL} element={<AnnualOvertimeDetail />} />
              <Route path={ADMIN_ROUTE_NAME.ANNUAL_CONFIRMATION} element={<AnnualOvertimeDetail />} />
            </Route>
          </Route>

          {/* using this route below when the route is public */}
          <Route>
            {/* components */}
            <Route path="components" element={<CommonComponents />} />
            <Route path="contact" element={<Contact />} />
          </Route>

          {/* Not Found */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      }
    />
  );
};

export default AdminRoutes;
